import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A visual representation that displays data points connected by lines, with the area
          beneath the lines filled to emphasize the magnitude of values over time or across
          categories. It is particularly effective for highlighting trends, cumulative totals, or
          proportional relationships. While useful for showing overall changes, it can sometimes
          obscure detailed variations when multiple series overlap.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Area charts are best suited for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Trends Over Time:</strong> Effectively display how a variable
            evolves over time, emphasizing the magnitude of changes, such as daily website visits,
            annual revenue, or temperature variations.
          </li>
          <li>
            <strong>Highlighting Magnitude:</strong> The filled area under the line makes it easy to
            understand the volume or intensity of data, such as energy consumption, rainfall levels,
            or resource utilization over specific periods.
          </li>
          <li>
            <strong>Showing Cumulative Data:</strong> Are ideal for representing cumulative values,
            helping to illustrate how totals build up, such as cumulative sales, accumulated
            savings, or growing populations.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          While area charts are a helpful tool for visualizing data, they have certain limitations
          that should be taken into account:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Overlapping Data Series:</strong> When multiple series are displayed in stacked
            area charts, overlapping areas can make it difficult to distinguish between individual
            categories, especially if the differences are minimal.
          </li>
          <li>
            <strong>Difficulty Representing Negative Values:</strong> Area charts can struggle to
            accurately represent negative values, as the filled areas may create confusion when the
            data fluctuates above and below the baseline.
          </li>
          <li>
            <strong>Clutter with Complex Data:</strong> For datasets with many categories or
            frequent fluctuations, area charts can become crowded and hard to read, potentially
            obscuring important trends and making interpretation challenging.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection plays a crucial role in area charts to enhance clarity and emphasize
          trends. Distinct, contrasting colors help differentiate between multiple data series,
          making it easier to identify individual categories. For stacked area charts, using a
          consistent color palette can emphasize the contribution of each category to the total.
          Gradual color transitions can highlight intensity or progression over time, while a
          diverging color scheme may be useful to show changes around a central value. Careful color
          choices ensure that the chart remains readable and visually effective.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          While area charts are useful for visualizing data, they come with challenges that should
          be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty with Complex Data:</strong> When dealing with complex datasets or
            multiple variables, area charts can become cluttered, making it harder to discern clear
            patterns or trends.
          </li>
          <li>
            <strong>Limited Use for Discrete Data:</strong> Area charts are best suited for
            continuous data. Using them with discrete data points can lead to misleading
            interpretations, as the chart assumes continuity between data points.
          </li>
          <li>
            <strong>Issues with Small Data Variations:</strong> In datasets with small fluctuations
            or large ranges, area charts may not effectively highlight subtle changes, potentially
            masking important details or trends.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
